import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Eye, EyeOff, UploadCloud, Loader2, Sparkles, Wand2, Check } from "lucide-react";
import { api } from "../../lib/api";
import { Modal, IconBtn, AddBtn, inputCls } from "./ui";

const PLACEMENTS = { home_top: "Home — top", shop_top: "Shop — top" };

const todayStr = () => new Date().toISOString().slice(0, 10);
function scheduleLabel(b) {
  const t = todayStr();
  if (b.start_at && t < b.start_at) return `Scheduled — starts ${b.start_at}`;
  if (b.end_at && t > b.end_at) return `Expired on ${b.end_at}`;
  if (b.end_at) return `Live until ${b.end_at}`;
  if (b.start_at) return `Live since ${b.start_at}`;
  return "";
}

export default function Banners() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState(null);
  const load = async () => { const { data } = await api.get("/admin/banners"); setBanners(data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this banner?")) return; await api.delete(`/admin/banners/${id}`); toast.success("Banner deleted"); load(); };
  const toggle = async (b) => { await api.put(`/admin/banners/${b.id}`, { ...b, active: !b.active }); load(); };

  return (
    <div data-testid="admin-banners">
      <div className="flex justify-between items-center gap-2 mb-4">
        <p className="text-sm text-[#5C5C5C]">Upload promo images and choose where they appear. Shoppers can dismiss them.</p>
        <AddBtn label="New banner" onClick={() => setForm({})} testid="add-banner-btn" />
      </div>

      <AIBannerBuilder onBuilt={load} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {banners.length === 0 && <p className="text-[#5C5C5C]">No banners yet.</p>}
        {banners.map((b) => (
          <div key={b.id} className={`bg-white border border-black/5 rounded-2xl overflow-hidden ${b.active ? "" : "opacity-55"}`} data-testid={`banner-row-${b.id}`}>
            <div className="aspect-[1200/260] bg-[#E7E1B1]"><img src={b.image} alt={b.title} className="w-full h-full object-cover" /></div>
            <div className="p-4 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-display text-lg text-[#0D530E] truncate">{b.title || "Untitled"}</p>
                <p className="text-xs text-[#5C5C5C]">{PLACEMENTS[b.placement] || b.placement}{b.link ? " · links out" : ""}</p>
                {(b.start_at || b.end_at) && <p className="text-[11px] text-[#306D29] mt-0.5">{scheduleLabel(b)}</p>}
              </div>
              <div className="flex gap-0.5 shrink-0">
                <IconBtn title={b.active ? "Hide" : "Show"} testid={`banner-active-${b.id}`} onClick={() => toggle(b)}>{b.active ? <Eye size={15} /> : <EyeOff size={15} />}</IconBtn>
                <IconBtn title="Edit" testid={`banner-edit-${b.id}`} onClick={() => setForm(b)}><Pencil size={15} /></IconBtn>
                <IconBtn title="Delete" danger testid={`banner-del-${b.id}`} onClick={() => del(b.id)}><Trash2 size={15} /></IconBtn>
              </div>
            </div>
          </div>
        ))}
      </div>
      {form && <BannerForm banner={form} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

function AIBannerBuilder({ onBuilt }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [placement, setPlacement] = useState("home_top");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState(null); // { images:[], title, link, placement, start_at, end_at }
  const [publishing, setPublishing] = useState(false);

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the campaign first"); return; }
    setBusy(true); setOptions(null);
    try {
      const { data } = await api.post("/admin/banners/ai-build", {
        prompt: prompt.trim(), placement, start_at: startAt, end_at: endAt, variations: 3, create: false,
      });
      if (!data?.images?.length) { toast.error("No images returned"); return; }
      setOptions(data);
      toast.success(`Generated ${data.images.length} options — pick one to publish`);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI banner failed"); }
    finally { setBusy(false); }
  };

  const publish = async (image) => {
    setPublishing(true);
    try {
      await api.post("/admin/banners", {
        title: options.title || "AI campaign", image, link: options.link || "",
        placement: options.placement || placement, active: true, order: 0,
        start_at: options.start_at || startAt, end_at: options.end_at || endAt,
      });
      toast.success(`Banner published${options.start_at ? ` — live from ${options.start_at}` : " — live now"}`);
      setPrompt(""); setStartAt(""); setEndAt(""); setOptions(null); setOpen(false);
      onBuilt?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setPublishing(false); }
  };

  return (
    <div className="mb-5 rounded-2xl border border-[#306D29]/20 bg-[#E7E1B1]/30 p-4" data-testid="ai-banner-builder">
      <button onClick={() => setOpen((v) => !v)} data-testid="ai-banner-toggle"
        className="flex items-center gap-2 text-sm font-semibold text-[#0D530E]">
        <Wand2 size={16} className="text-[#306D29]" /> Build a banner with AI
        <span className="text-xs font-normal text-[#5C5C5C]">— describe a sale, get 3 designs, pick your favourite to schedule</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <textarea rows={2} className={inputCls} data-testid="ai-banner-prompt"
            placeholder="e.g. 20% off all Beauty products this weekend — bright, festive, shows lipsticks and skincare"
            value={prompt} onChange={(e) => setPrompt(e.target.value)} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-[#5C5C5C]">Where</span>
              <select className={inputCls} value={placement} onChange={(e) => setPlacement(e.target.value)} data-testid="ai-banner-placement">
                <option value="home_top">Home — top</option>
                <option value="shop_top">Shop — top</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#5C5C5C]">Start (optional)</span>
              <input type="date" className={inputCls} value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="ai-banner-start" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#5C5C5C]">End (optional)</span>
              <input type="date" className={inputCls} value={endAt} onChange={(e) => setEndAt(e.target.value)} data-testid="ai-banner-end" />
            </label>
          </div>
          <button onClick={generate} disabled={busy || publishing} data-testid="ai-banner-build-btn"
            className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
            {busy ? <><Loader2 size={16} className="animate-spin" /> Generating 3 designs…</> : <><Sparkles size={16} /> {options ? "Regenerate 3 options" : "Generate 3 options"}</>}
          </button>
          <p className="text-[11px] text-[#5C5C5C]">Leave dates empty to go live now with no expiry. The AI reads dates from your text too (e.g. "from Aug 5 to Aug 15").</p>

          {options?.images?.length > 0 && (
            <div className="pt-1" data-testid="ai-banner-options">
              <p className="text-xs font-semibold text-[#0D530E] mb-2">Pick a design to publish{options.title ? ` — “${options.title}”` : ""}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {options.images.map((img, i) => (
                  <div key={i} className="bg-white rounded-xl overflow-hidden border border-black/5" data-testid={`ai-banner-option-${i}`}>
                    <div className="aspect-[1200/260] bg-[#E7E1B1]"><img src={img} alt={`Option ${i + 1}`} className="w-full h-full object-cover" /></div>
                    <button onClick={() => publish(img)} disabled={publishing} data-testid={`ai-banner-publish-${i}`}
                      className="w-full bg-[#2D7A5D] hover:bg-[#0D530E] text-white text-sm font-semibold py-2.5 flex items-center justify-center gap-1.5 disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
                      {publishing ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Use option {i + 1}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BannerForm({ banner, onClose, onSaved }) {
  const editing = !!banner.id;
  const [f, setF] = useState({
    title: banner.title || "", image: banner.image || "", link: banner.link || "",
    placement: banner.placement || "home_top", active: banner.active ?? true, order: banner.order || 0,
    start_at: banner.start_at || "", end_at: banner.end_at || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, image: data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!f.image) { toast.error("Please upload a banner image"); return; }
    setSaving(true);
    const body = { ...f, order: parseInt(f.order) || 0 };
    try {
      if (editing) await api.put(`/admin/banners/${banner.id}`, body);
      else await api.post("/admin/banners", body);
      toast.success(editing ? "Banner saved" : "Banner created");
      onSaved();
    } catch { toast.error("Failed to save banner"); setSaving(false); }
  };

  return (
    <Modal title={editing ? "Edit banner" : "New banner"} onClose={onClose} testid="banner-form">
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="text-xs font-semibold text-[#5C5C5C]">Banner image (wide, ~1200×260)</span>
          <div className="mt-1 border-2 border-dashed border-[#306D29]/30 rounded-xl p-4 text-center cursor-pointer hover:bg-[#E7E1B1]/40" style={{ transition: "background-color 0.2s ease" }}>
            <input type="file" accept="image/*" onChange={upload} className="hidden" id="banner-file" data-testid="bnf-file" />
            <label htmlFor="banner-file" className="cursor-pointer flex flex-col items-center gap-1.5 text-[#306D29]">
              {uploading ? <Loader2 size={22} className="animate-spin" /> : <UploadCloud size={22} />}
              <span className="text-sm font-medium">{uploading ? "Uploading…" : "Click to upload image"}</span>
            </label>
          </div>
        </label>
        {f.image && <img src={f.image} alt="" className="w-full aspect-[1200/260] object-cover rounded-xl bg-[#E7E1B1]" data-testid="bnf-preview" />}
        <input className={inputCls} placeholder="Title (internal)" value={f.title} onChange={set("title")} data-testid="bnf-title" />
        <input className={inputCls} placeholder="Link URL when clicked (optional, e.g. /shop?category=Beauty)" value={f.link} onChange={set("link")} data-testid="bnf-link" />
        <div className="grid grid-cols-2 gap-3">
          <select className={inputCls} value={f.placement} onChange={set("placement")} data-testid="bnf-placement">
            <option value="home_top">Home — top</option>
            <option value="shop_top">Shop — top</option>
          </select>
          <input type="number" className={inputCls} placeholder="Order" value={f.order} onChange={set("order")} data-testid="bnf-order" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-semibold text-[#5C5C5C]">Show from (optional)</span>
            <input type="date" className={inputCls} value={f.start_at} onChange={set("start_at")} data-testid="bnf-start" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-[#5C5C5C]">Hide after (optional)</span>
            <input type="date" className={inputCls} value={f.end_at} onChange={set("end_at")} data-testid="bnf-end" />
          </label>
        </div>
        <p className="text-[11px] text-[#5C5C5C] -mt-1">Leave dates empty to show the banner immediately with no expiry. Scheduling only applies while the banner is Active.</p>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="bnf-active" /> Active (visible on site)</label>
        <button type="submit" disabled={saving || uploading} data-testid="bnf-submit" className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{saving ? "Saving…" : editing ? "Save banner" : "Create banner"}</button>
      </form>
    </Modal>
  );
}
